import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import BackButton from '@/components/BackButton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Star, Users, CheckCircle, ChatText, Download, ChartBar } from '@phosphor-icons/react';
import { useLanguage } from '@/contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = '/branding/logo.png';

export default function AdminSurveyResults() {
  const { id } = useParams();
  const { language, direction } = useLanguage();
  const isAr = language === 'ar';
  const T = (ar, en) => (isAr ? ar : en);
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/surveys/${id}/results`, { withCredentials: true });
        setData(r.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || T('فشل تحميل النتائج', 'Failed to load'));
      } finally { setLoading(false); }
    })();
  }, [id]);

  if (loading) return <div className="p-8 text-center text-[#655dc6]/60">{T('جار التحميل...', 'Loading...')}</div>;
  if (!data) return <div className="p-8 text-center">{T('لا توجد بيانات', 'No data')}</div>;

  const { survey, results, total_responses, respondents, pending, audience_summary } = data;
  const responseRate = survey.recipient_count ? Math.round(100 * total_responses / survey.recipient_count) : 0;

  const exportCsv = () => {
    const rows = [['Question', 'Type', 'Answer / Rating', 'Count']];
    for (const [qid, slot] of Object.entries(results || {})) {
      const qtitle = isAr ? slot.question.title_ar : (slot.question.title_en || slot.question.title_ar);
      if (slot.tally) {
        for (const [opt, cnt] of Object.entries(slot.tally)) {
          rows.push([qtitle, slot.question.type, opt, cnt]);
        }
      }
      if (slot.answers) {
        for (const a of slot.answers) {
          rows.push([qtitle, 'text', (a.text || '').replace(/"/g, '""'), '1']);
        }
      }
    }
    const csv = rows.map((r) => r.map((v) => `"${v}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = `survey_${id}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[#F2F0FA] dark:bg-slate-950" dir={direction} data-testid="admin-survey-results-page">
      <header className="sticky top-0 z-40 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm shadow-sm border-b border-[#C4BFF0] dark:border-slate-700 py-4">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <img src={LOGO_URL} alt="Lucent" className="h-10 object-contain" />
          <BackButton className="text-[#655dc6]" />
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-2xl md:text-3xl font-bold text-[#655dc6] dark:text-violet-300 flex items-center gap-2">
            <ChartBar size={28} weight="duotone" />
            {T('نتائج الاستبيان', 'Survey Results')}
          </h1>
          <Button variant="outline" onClick={exportCsv} data-testid="export-csv-btn">
            <Download size={14} className="me-1" /> {T('تصدير CSV', 'Export CSV')}
          </Button>
        </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-[#655dc6]">
            {isAr ? survey.title_ar : (survey.title_en || survey.title_ar)}
          </CardTitle>
          <div className="text-sm text-[#655dc6]/70">{isAr ? survey.description_ar : survey.description_en}</div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label={T('الجمهور', 'Audience')} value={audience_summary} icon={Users} />
            <StatCard label={T('مُرسل', 'Sent')} value={survey.recipient_count || 0} icon={Users} />
            <StatCard label={T('ردود', 'Responses')} value={total_responses} icon={CheckCircle} color="emerald" />
            <StatCard label={T('نسبة الرد', 'Response Rate')} value={`${responseRate}%`} icon={ChatText} color="violet" />
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4">
        {Object.entries(results || {}).map(([qid, slot], idx) => (
          <Card key={qid} data-testid={`result-${qid}`}>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-start gap-2">
                <span className="text-xs text-[#655dc6]/50 font-mono">Q{idx + 1}</span>
                <h3 className="font-semibold text-[#655dc6] flex-1">
                  {isAr ? slot.question.title_ar : (slot.question.title_en || slot.question.title_ar)}
                </h3>
              </div>
              {slot.question.type === 'single_choice' && slot.tally && (
                <ChoiceChart tally={slot.tally} total={total_responses} isAr={isAr} />
              )}
              {slot.question.type === 'rating' && slot.tally && (
                <RatingChart tally={slot.tally} scale={slot.question.scale || 5} average={slot.average} count={slot.count} isAr={isAr} />
              )}
              {slot.question.type === 'text' && (
                <div className="space-y-1 max-h-64 overflow-y-auto">
                  {(slot.answers || []).length === 0 ? (
                    <p className="text-sm text-[#655dc6]/60 text-center py-4">{T('لا توجد إجابات', 'No answers yet')}</p>
                  ) : (
                    (slot.answers || []).map((a, i) => (
                      <div key={i} className="p-2 bg-[#F8F7FC] dark:bg-slate-800 rounded text-sm">
                        <div className="text-[#655dc6] dark:text-violet-300">{a.text}</div>
                        {a.user_name && <div className="text-[10px] text-[#655dc6]/50 mt-0.5">— {a.user_name}</div>}
                      </div>
                    ))
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {!survey.anonymous && respondents.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-sm">{T('قائمة المشاركين', 'Respondents')} ({respondents.length})</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-1 max-h-48 overflow-y-auto">
            {respondents.map((r, i) => (
              <div key={i} className="flex items-center justify-between py-1 border-b border-[#C4BFF0]/30 last:border-0">
                <span>{r.user_name || r.user_id}</span>
                <span className="text-[10px] text-[#655dc6]/60">{new Date(r.submitted_at).toLocaleString()}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {pending.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">
              {T(`لم يردوا بعد (${pending.length})`, `Not responded yet (${pending.length})`)}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-xs max-h-32 overflow-y-auto">
            {pending.map((p) => (isAr ? p.name : (p.name_en || p.name))).join('، ')}
          </CardContent>
        </Card>
      )}
      </main>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color = 'violet' }) {
  const colors = {
    violet: 'text-[#655dc6]',
    emerald: 'text-emerald-600',
  };
  return (
    <div className="p-3 bg-[#F8F7FC] dark:bg-slate-800 rounded-lg">
      <div className="flex items-center gap-2 text-xs text-[#655dc6]/70 mb-1">
        <Icon size={14} /> {label}
      </div>
      <div className={`text-xl font-bold ${colors[color]}`}>{value}</div>
    </div>
  );
}

function ChoiceChart({ tally, total, isAr }) {
  const entries = Object.entries(tally).filter(([k]) => k !== '__other__');
  const max = Math.max(1, ...entries.map(([, v]) => v));
  return (
    <div className="space-y-1.5">
      {entries.map(([opt, cnt]) => {
        const pct = Math.round(100 * cnt / Math.max(1, total));
        return (
          <div key={opt}>
            <div className="flex justify-between text-xs mb-0.5">
              <span className="text-[#655dc6]">{opt}</span>
              <span className="text-[#655dc6]/70 font-mono">{cnt} ({pct}%)</span>
            </div>
            <div className="h-2 bg-[#C4BFF0]/30 rounded overflow-hidden">
              <div className="h-full bg-[#655dc6]" style={{ width: `${100 * cnt / max}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function RatingChart({ tally, scale, average, count, isAr }) {
  const T = (ar, en) => (isAr ? ar : en);
  const max = Math.max(1, ...Object.values(tally));
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 justify-center">
        <div className="text-3xl font-bold text-amber-500">{average || 0}</div>
        <div>
          <div className="flex">
            {Array.from({ length: scale }).map((_, i) => (
              <Star key={i} size={16} weight={i < Math.round(average || 0) ? 'fill' : 'regular'} className="text-amber-400" />
            ))}
          </div>
          <div className="text-xs text-[#655dc6]/60">{count} {T('مقيّم', 'raters')}</div>
        </div>
      </div>
      <div className="space-y-1">
        {Array.from({ length: scale }).map((_, i) => {
          const star = scale - i;
          const cnt = tally[String(star)] || 0;
          return (
            <div key={star} className="flex items-center gap-2 text-xs">
              <span className="w-6 text-end">{star}</span>
              <Star size={10} weight="fill" className="text-amber-400" />
              <div className="flex-1 h-1.5 bg-[#C4BFF0]/30 rounded overflow-hidden">
                <div className="h-full bg-amber-400" style={{ width: `${100 * cnt / max}%` }} />
              </div>
              <span className="w-8 font-mono text-end">{cnt}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
